import { type ReactNode } from "react";
import { type LucideIcon } from "lucide-react";
import { cn } from "@/shared/lib/utils";

type Tone = "neutral" | "brand";

type Props = {
  icon: LucideIcon;
  tone?: Tone;
};

// The tile stays white in both tones: brand is used on a tinted card, where a
// tinted tile would sink into the fill and a white one lifts off it.
const tileClasses: Record<Tone, string> = {
  neutral: "border-neutral-200",
  brand: "border-blue-200",
};

const glyphClasses: Record<Tone, string> = {
  neutral: "text-neutral-700",
  brand: "text-blue-600",
};

// Untitled UI "featured icon", modern style: the icon sits in its own bordered
// white tile rather than floating loose beside the text. 32px tile, 16px glyph
// — one size, used for side panel cards, list rows and modal headers alike.
export const FeaturedIcon = ({ icon: Icon, tone = "neutral" }: Props) => (
  <span
    className={cn(
      "flex size-8 shrink-0 items-center justify-center rounded-lg border bg-white shadow-xs",
      tileClasses[tone],
    )}
  >
    <Icon className={cn("size-4", glyphClasses[tone])} />
  </span>
);

// The same tile at the size an empty state and the error pages use. It takes a
// rendered icon rather than the component, because those surfaces receive their
// icon as a node from the caller.
export const FeaturedIconTile = ({ children }: { children: ReactNode }) => (
  <span className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-neutral-200 bg-white text-neutral-600 shadow-xs [&>svg]:size-6">
    {children}
  </span>
);
